import { AxiosError } from "axios";
import {
  PresetSummary,
  UsageByMonth,
} from "../model/analytics.types";
import AnalyticsService, {
  GetPresetsSummaryParams,
  GetPresetsUsageByPeriodParams,
  PresetsUsageByPeriodResponse,
  GetPacksAnalyticsParams,
  PacksAnalyticsResponse,
  PackAnalyticsGroupedItem,
} from "../services/analyticsService";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useState, useEffect, useMemo } from "react";



export function useAnalytics(
  params: GetPacksAnalyticsParams = {
    start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  }
) {
  // Parâmetros para summary e usage (sem paginação)
  const summaryParams = { start: params.start!, end: params.end! };

  const { data: summary, isLoading: loadingSummary, error: summaryError } = useCustomQuery<
    AnalyticsService,
    GetPresetsSummaryParams,
    PresetSummary
  >({
    serviceInstance: AnalyticsService.Instance,
    selectAction: (s) => s.getPresetsSummary,
    parameter: summaryParams,
    options: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
    },
  });

  const { data: usageData, isLoading: loadingUsage, error: usageError } = useCustomQuery<
    AnalyticsService,
    GetPresetsUsageByPeriodParams,
    PresetsUsageByPeriodResponse
  >({
    serviceInstance: AnalyticsService.Instance,
    selectAction: (s) => s.getPresetsUsageByPeriod,
    parameter: summaryParams,
  });

  // Estado para manter dados anteriores durante paginação
  const [previousPacksData, setPreviousPacksData] = useState<
    PacksAnalyticsResponse<PackAnalyticsGroupedItem> | undefined
  >(undefined);

  // Query para os dados de pacotes
  const {
    data: packsData,
    isLoading: loadingPacks,
    error: packsError,
  } = useCustomQuery<
    AnalyticsService,
    GetPacksAnalyticsParams,
    PacksAnalyticsResponse<PackAnalyticsGroupedItem>
  >({
    serviceInstance: AnalyticsService.Instance,
    selectAction: (s) => s.getPacksAnalytics,
    parameter: params,
    options: {
      staleTime: 30 * 1000, // 30 segundos
    },
  });

  // Usar dados anteriores se estiver carregando e houver dados anteriores
  const currentPacksData = loadingPacks && previousPacksData ? previousPacksData : packsData;

  // Atualiza o total de páginas quando os dados mudam
  const totalPages = useMemo(() => {
    return currentPacksData?.totalPage || 1;
  }, [currentPacksData]);

  // Efeito para gerenciar dados anteriores durante paginação
  useEffect(() => {
    if (packsData && !loadingPacks) {
      setPreviousPacksData(packsData);
    }
  }, [packsData, loadingPacks]);

  const usage: UsageByMonth[] = useMemo(
    () => {
      return usageData?.data?.map((item) => ({
        month: item.month,
        paid: item.paidUses || 0,
        free: item.freeUses || 0,
        total: (item.paidUses || 0) + (item.freeUses || 0),
      })) || [];
    },
    [usageData]
  );

  return {
    summary,
    usage,
    packsData,
    loading: {
      all: loadingSummary || loadingUsage || loadingPacks,
      summary: loadingSummary,
      usage: loadingUsage,
      packs: loadingPacks && !previousPacksData, // Só mostra loading se não tiver dados anteriores
    },
    errors: {
      summary: summaryError as AxiosError,
      usage: usageError as AxiosError,
      packs: packsError as AxiosError,
    },
    totalPages,
    // Indica se está carregando nova página mas tem dados anteriores
    isLoadingNewPage: loadingPacks && !!previousPacksData,
  };
}
